import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';

export default function DestinationLibrary() {
  const { authFetch } = useAuth();
  const [hierarchy, setHierarchy] = useState({});
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({ continents: {}, countries: {} });
  const [addingTo, setAddingTo] = useState(null); // { continent, country, area }
  const [addForm, setAddForm] = useState({ continent: '', country: '', area: '', title: '', photo_url: '' });
  const [addMode, setAddMode] = useState('url'); // 'url' | 'file'
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const fileRef = useRef(null);
  const [newArea, setNewArea] = useState({ continent: '', country: '', area: '' });
  const [showNewArea, setShowNewArea] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await authFetch('/api/destination-photos');
      const data = await res.json();
      setHierarchy(data.hierarchy || {});
    } catch (e) {}
    setLoading(false);
  };

  const toggleContinent = (c) => setExpanded(e => ({ ...e, continents: { ...e.continents, [c]: !e.continents[c] } }));
  const toggleCountry = (k) => setExpanded(e => ({ ...e, countries: { ...e.countries, [k]: !e.countries[k] } }));

  const openAdd = (continent, country, area) => {
    setAddingTo({ continent, country, area });
    setAddForm({ continent, country, area, title: area, photo_url: '' });
    setSaveMsg('');
  };

  const handleSave = async () => {
    if (!addForm.continent || !addForm.country || !addForm.area) return;
    setSaving(true);
    setSaveMsg('');
    try {
      const tok = localStorage.getItem('tripdash_token');
      const fd = new FormData();
      fd.append('continent', addForm.continent);
      fd.append('country', addForm.country);
      fd.append('area', addForm.area);
      fd.append('title', addForm.title || addForm.area);
      if (addMode === 'url') {
        fd.append('photo_url', addForm.photo_url);
      } else if (fileRef.current?.files?.[0]) {
        fd.append('photo', fileRef.current.files[0]);
      }
      const res = await fetch('/api/destination-photos', {
        method: 'POST',
        headers: { Authorization: `Bearer ${tok}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setSaveMsg('Foto opgeslagen!');
      setAddingTo(null);
      load();
    } catch (e) {
      setSaveMsg('Fout: ' + e.message);
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    await authFetch(`/api/destination-photos/${id}`, { method: 'DELETE' });
    load();
  };

  const handleAddNewArea = async () => {
    if (!newArea.continent || !newArea.country || !newArea.area) return;
    openAdd(newArea.continent, newArea.country, newArea.area);
    setShowNewArea(null);
  };

  if (loading) return (
    <div className="p-8 flex items-center justify-center h-64">
      <div className="spinner spinner-blue" style={{ width: 32, height: 32 }} />
    </div>
  );

  return (
    <div className="p-8 max-w-5xl">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-900">Foto Bibliotheek</h1>
        <p className="text-gray-500 text-sm mt-1">Beheer bestemmingsfoto's die de AI gebruikt bij het genereren van reisvoorstellen</p>
      </div>

      {saveMsg && (
        <div className={`mb-4 p-3 rounded-lg text-sm border ${saveMsg.startsWith('Fout') ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-700'}`}>
          {saveMsg}
        </div>
      )}

      <div className="card">
        {Object.entries(hierarchy).map(([continent, countries]) => (
          <div key={continent} className="mb-2">
            {/* Continent header */}
            <button onClick={() => toggleContinent(continent)}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left font-bold text-white transition-all"
              style={{ background: 'linear-gradient(135deg, #1a6bab, #0d4a7a)' }}>
              <span className="text-lg">{continent === 'Europa' ? '🌍' : '🌎'}</span>
              <span className="flex-1">{continent}</span>
              <svg className={`w-4 h-4 transition-transform ${expanded.continents[continent] ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {expanded.continents[continent] && (
              <div className="ml-4 mt-2 space-y-2">
                {Object.entries(countries).map(([country, areas]) => {
                  const countryKey = `${continent}-${country}`;
                  const totalPhotos = Object.values(areas).reduce((s, ps) => s + ps.length, 0);
                  return (
                    <div key={country}>
                      <button onClick={() => toggleCountry(countryKey)}
                        className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-left bg-gray-50 hover:bg-gray-100 border border-gray-200 transition-all">
                        <span className="font-semibold text-gray-800 flex-1">{country}</span>
                        <span className="text-xs text-gray-400 bg-white px-2 py-0.5 rounded-full border border-gray-200">{totalPhotos} foto's</span>
                        <svg className={`w-4 h-4 text-gray-400 transition-transform ${expanded.countries[countryKey] ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>

                      {expanded.countries[countryKey] && (
                        <div className="ml-4 mt-2 space-y-3">
                          {Object.entries(areas).map(([area, photos]) => (
                            <div key={area} className="border border-gray-200 rounded-xl overflow-hidden">
                              <div className="flex items-center justify-between px-4 py-2.5 bg-white border-b border-gray-100">
                                <span className="font-medium text-gray-700 text-sm">📍 {area}</span>
                                <button onClick={() => openAdd(continent, country, area)}
                                  className="w-7 h-7 rounded-full flex items-center justify-center text-white text-lg font-bold hover:opacity-80 transition-opacity"
                                  style={{ backgroundColor: 'var(--brand)' }} title="Foto toevoegen">
                                  +
                                </button>
                              </div>
                              <div className="p-3 flex gap-2 flex-wrap bg-gray-50">
                                {photos.map(photo => (
                                  <div key={photo.id} className="relative group">
                                    <img src={photo.photo_url} alt={photo.title}
                                      className="w-24 object-cover rounded-lg border border-gray-200"
                                      style={{ height: 72 }}
                                      onError={e => { e.target.src = 'https://images.unsplash.com/photo-1488085061387-422e29b40080?w=200&q=60'; }} />
                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 rounded-lg transition-all" />
                                    <button onClick={() => handleDelete(photo.id)}
                                      className="absolute top-1 left-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center leading-none hover:bg-red-600"
                                      title="Foto verwijderen">
                                      ×
                                    </button>
                                    <span className="absolute bottom-0 left-0 right-0 text-white text-xs text-center pb-1 opacity-0 group-hover:opacity-100 transition-opacity bg-gradient-to-t from-black/60 rounded-b-lg truncate px-1">
                                      {photo.title}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}

                          {/* Add new area button */}
                          {showNewArea === countryKey ? (
                            <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl">
                              <p className="text-xs font-semibold text-blue-700 mb-2">Nieuwe regio toevoegen in {country}</p>
                              <input className="input text-sm mb-2" placeholder="Naam van de regio (bijv. Costa Brava)"
                                value={newArea.area} onChange={e => setNewArea({ continent, country, area: e.target.value })} />
                              <div className="flex gap-2">
                                <button onClick={() => handleAddNewArea()} className="btn-primary text-xs py-1.5">Toevoegen + foto uploaden</button>
                                <button onClick={() => setShowNewArea(null)} className="btn-secondary text-xs py-1.5">Annuleren</button>
                              </div>
                            </div>
                          ) : (
                            <button onClick={() => { setShowNewArea(countryKey); setNewArea({ continent, country, area: '' }); }}
                              className="w-full py-2 rounded-xl border-2 border-dashed border-gray-300 text-gray-400 hover:border-blue-300 hover:text-blue-500 transition-all text-sm">
                              + Nieuwe regio toevoegen
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Add photo modal */}
      {addingTo && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-900">Foto toevoegen — {addingTo.area}</h3>
              <button onClick={() => setAddingTo(null)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <p className="text-xs text-gray-500 mb-4">{addingTo.continent} › {addingTo.country} › {addingTo.area}</p>

            <div className="mb-4">
              <label className="label">Fototitel</label>
              <input className="input" placeholder={addingTo.area} value={addForm.title}
                onChange={e => setAddForm({ ...addForm, title: e.target.value })} />
            </div>

            <div className="flex gap-2 mb-4">
              <button onClick={() => setAddMode('url')}
                className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-all ${addMode === 'url' ? 'text-white border-transparent' : 'bg-gray-50 border-gray-200 text-gray-600'}`}
                style={addMode === 'url' ? { backgroundColor: 'var(--brand)' } : {}}>
                🔗 URL
              </button>
              <button onClick={() => setAddMode('file')}
                className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-all ${addMode === 'file' ? 'text-white border-transparent' : 'bg-gray-50 border-gray-200 text-gray-600'}`}
                style={addMode === 'file' ? { backgroundColor: 'var(--brand)' } : {}}>
                📁 Bestand uploaden
              </button>
            </div>

            {addMode === 'url' ? (
              <div className="mb-4">
                <label className="label">Foto URL</label>
                <input className="input" placeholder="https://images.unsplash.com/photo-..." value={addForm.photo_url}
                  onChange={e => setAddForm({ ...addForm, photo_url: e.target.value })} />
                {addForm.photo_url && (
                  <img src={addForm.photo_url} alt="Preview" className="mt-2 w-full h-32 object-cover rounded-lg border border-gray-200"
                    onError={e => { e.target.style.display = 'none'; }} />
                )}
              </div>
            ) : (
              <div className="mb-4">
                <label className="label">Afbeeldingsbestand</label>
                <input ref={fileRef} type="file" accept="image/*" className="input" />
              </div>
            )}

            {saveMsg && <p className={`text-sm mb-3 ${saveMsg.startsWith('Fout') ? 'text-red-600' : 'text-green-600'}`}>{saveMsg}</p>}

            <div className="flex gap-3">
              <button onClick={handleSave} disabled={saving} className="btn-primary flex-1 justify-center">
                {saving ? <span className="spinner" /> : '💾'}
                {saving ? 'Opslaan...' : 'Foto opslaan'}
              </button>
              <button onClick={() => setAddingTo(null)} className="btn-secondary">Annuleren</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
